import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyReferral, applyReferralCode, getReferralLeaderboard } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Gift, Copy, Users, DollarSign, Trophy, Check } from 'lucide-react';
import { toast } from 'sonner';

const getRankStyle = (idx) => {
  if (idx === 0) return 'bg-[#F59E0B]/20 text-[#F59E0B]';
  if (idx === 1) return 'bg-[#8C9A76]/20 text-[#8C9A76]';
  if (idx === 2) return 'bg-[#D97757]/20 text-[#D97757]';
  return 'bg-[#EBE8DF] text-[#596652]';
};

const ReferralPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [referral, setReferral] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    const fetch = async () => {
      try {
        const [ref, lb] = await Promise.all([getMyReferral(), getReferralLeaderboard()]);
        setReferral(ref);
        setLeaderboard(lb);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [isAuthenticated, authLoading, navigate]);

  const handleCopyCode = () => {
    if (!referral?.code) return;
    navigator.clipboard.writeText(referral.code);
    setCopied(true);
    toast.success('Referral code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    if (!referral?.code) return;
    const link = `${window.location.origin}/register?ref=${referral.code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleApply = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const res = await applyReferralCode(code.trim());
      toast.success(res.message);
      const ref = await getMyReferral();
      setReferral(ref);
      setCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply code');
    } finally { setApplying(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center"><div className="animate-pulse text-[#596652]">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-[#D97757]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-[#D97757]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#2C3329] mb-3">Referral Program</h1>
          <p className="text-[#596652] max-w-md mx-auto">Invite your neighbors and earn $5 credit for every friend who joins. They get $5 too!</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* My Referral Code */}
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6" data-testid="referral-code-section">
            <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">Your Referral Code</h2>
            
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 bg-[#FDFBF7] border border-[#EBE8DF] rounded-xl px-4 py-3 font-mono text-lg text-[#2C3329] text-center tracking-wider" data-testid="my-referral-code">
                {referral?.code || '...'}
              </div>
              <Button
                variant="outline"
                onClick={handleCopyCode}
                className="border-[#EBE8DF] rounded-xl px-4"
                data-testid="copy-code-button"
              >
                {copied ? <Check className="w-4 h-4 text-[#4CAF50]" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <Button
              onClick={handleCopyLink}
              className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
              data-testid="copy-link-button"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Invite Link
            </Button>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-[#FDFBF7] rounded-xl p-4 text-center">
                <DollarSign className="w-5 h-5 text-[#4CAF50] mx-auto mb-1" />
                <p className="text-2xl font-heading font-bold text-[#2C3329]">${referral?.credits?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-[#596652]">Credits Earned</p>
              </div>
              <div className="bg-[#FDFBF7] rounded-xl p-4 text-center">
                <Users className="w-5 h-5 text-[#D97757] mx-auto mb-1" />
                <p className="text-2xl font-heading font-bold text-[#2C3329]">{referral?.total_referrals || 0}</p>
                <p className="text-xs text-[#596652]">Friends Invited</p>
              </div>
            </div>
          </div>

          {/* Apply Code + Leaderboard */}
          <div className="space-y-6">
            {/* Apply a Code */}
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6" data-testid="apply-code-section">
              <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4">Have a Referral Code?</h2>
              <div className="flex gap-2">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter friend's code"
                  className="border-[#EBE8DF] rounded-xl"
                  data-testid="apply-code-input"
                />
                <Button
                  onClick={handleApply}
                  disabled={applying || !code.trim()}
                  className="bg-[#8C9A76] text-white rounded-xl hover:bg-[#7A8A64] px-6"
                  data-testid="apply-code-button"
                >
                  {applying ? '...' : 'Apply'}
                </Button>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6" data-testid="referral-leaderboard">
              <h2 className="font-heading font-semibold text-lg text-[#2C3329] mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#F59E0B]" />
                Top Referrers
              </h2>
              {leaderboard.length > 0 ? (
                <div className="space-y-3">
                  {leaderboard.map((entry, idx) => (
                    <div key={entry.user_id} className="flex items-center justify-between py-2 border-b border-[#EBE8DF] last:border-0">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getRankStyle(idx)}`}>{idx + 1}</span>
                        <span className="text-[#2C3329] font-medium">{entry.name}</span>
                      </div>
                      <span className="text-[#596652] text-sm">{entry.total_referrals} referrals</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#596652] text-sm text-center py-4">No referrals yet. Be the first!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
